/**
 * DTO → view model for My Files (HS-1441).
 *
 * Permanent: it survives the removal of `src/tmp/`, because what changes when the endpoints land
 * is where the DTO comes from, not what it means. Every defensive branch here is about a value
 * the contract allows (`null` type, `null` origin) or a value a real backend can still send wrong
 * (an unparseable date, a negative size, an enum member added later) — the screen renders a row
 * either way rather than throwing inside a render.
 *
 * Formatting is deliberately absent: it needs the active language and lives in `filesFormat.ts`.
 */
import type {
  CloudProvider,
  CloudProviderDto,
  DeleteManyDto,
  DeleteOutcome,
  DeleteOutcomeDto,
  DeleteResult,
  DownloadDto,
  DownloadTicket,
  FileCounts,
  FileCountsDto,
  FileGlyph,
  FileListDto,
  FileListItemDto,
  FileLocation,
  FileLocationDto,
  FileNotice,
  FileNoticeDto,
  FileNoticeKindDto,
  FileOriginDto,
  FilePreviewKind,
  FileRow,
  FileStatus,
  FileStatusDto,
  FilesView,
  FileType,
  FileTypeDto,
  Folder,
  FolderDto,
  MoveManyDto,
  MoveOutcome,
  MoveOutcomeDto,
  MoveResult,
  NodeCountsDto,
  StorageDto,
  StorageLevel,
  StorageView,
} from './files.types';
import {
  MAX_SELECTION_BYTES,
  MAX_SELECTION_FILES,
  PREVIEW_SHEET_MAX_BYTES,
  PREVIEW_TEXT_MAX_BYTES,
} from './filesConfig';

/** The meter turns warning here and error at {@link STORAGE_EXCEEDED_PERCENT} — contract §5. */
export const STORAGE_WARNING_PERCENT = 85;
export const STORAGE_EXCEEDED_PERCENT = 100;

const FULL_PERCENT = 100;
const FIRST_PAGE = 1;

const KNOWN_TYPES: FileTypeDto[] = ['report', 'exports', 'safetyDocs'];

const KNOWN_PROVIDERS: CloudProviderDto[] = ['oneDrive', 'googleDrive'];

const KNOWN_LOCATIONS: FileLocationDto[] = ['hawkins', ...KNOWN_PROVIDERS];

const KNOWN_STATUSES: FileStatusDto[] = [
  'synced',
  'unsynced',
  'noCredential',
  'conflict',
  'notFound',
  'blocked',
  'cached',
  'syncFailed',
  'quarantined',
  'credentialLost',
  'legalHold',
  'transferFailed',
];

const KNOWN_NOTICE_KINDS: FileNoticeKindDto[] = [
  'providerUnreachable',
  'tokenRevoked',
  'conflict',
  'notFound',
  'disabledByInstitution',
];

const KNOWN_OUTCOMES: DeleteOutcomeDto[] = ['deleted', 'legalHold', 'providerFailed', 'notFound'];

const KNOWN_MOVE_OUTCOMES: MoveOutcomeDto[] = ['moved', 'notFound', 'folderNotFound'];

/** Extensions the design draws with the red PDF glyph and the green spreadsheet glyph. */
const PDF_EXTENSIONS = ['pdf'];
const SPREADSHEET_EXTENSIONS = ['xlsx', 'xls', 'csv', 'tsv', 'ods', 'numbers'];
/** What the browser shows on its own through `<img>` (HS-1441A, HS-1474). */
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'avif', 'ico'];
/** Drawn with the image glyph but decoded by the portal (TIFF) or not previewed at all (HEIC). */
const TIFF_EXTENSIONS = ['tif', 'tiff'];
const HEIC_EXTENSIONS = ['heic', 'heif'];
/** Whether a given codec inside plays is the player's error, not the adapter's guess. */
const VIDEO_EXTENSIONS = ['mp4', 'm4v', 'webm', 'mov', 'ogv'];
/** The same holds for audio (HS-1474); `.ogg` is read as audio, `.ogv` is the video container. */
const AUDIO_EXTENSIONS = ['mp3', 'wav', 'm4a', 'aac', 'oga', 'ogg', 'opus', 'flac', 'weba'];

/**
 * What the preview page can render (HS-1441A, HS-1474). The extension is consulted first because
 * a browser-picked `file.type` is empty for `.md`, `.log` and most text a lab exports; the MIME
 * type is the fallback for a file whose extension says nothing.
 *
 * Deliberately download-only, whatever the MIME type says (sweep decision, 2026-09-22): the binary
 * Word `.doc` (no browser renderer exists), PowerPoint, and HEIC/HEIF (its only decoder is LGPL
 * libheif compiled to wasm).
 */
const TEXT_EXTENSIONS = ['txt', 'log', 'json', 'xml', 'yaml', 'yml', 'ini'];
const MARKDOWN_EXTENSIONS = ['md', 'markdown'];
const CSV_EXTENSIONS = ['csv', 'tsv'];
const DOCX_EXTENSIONS = ['docx'];
const SHEET_EXTENSIONS = ['xlsx'];
const LEGACY_SHEET_EXTENSIONS = ['xls', 'ods'];
const DOWNLOAD_ONLY_EXTENSIONS = ['doc', 'ppt', 'pptx', 'odp', ...HEIC_EXTENSIONS];
const TEXT_MIME_TYPES = ['application/json', 'application/xml'];
const MARKDOWN_MIME_TYPES = ['text/markdown', 'text/x-markdown'];
const CSV_MIME_TYPES = ['text/csv', 'text/tab-separated-values'];
const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const SHEET_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const LEGACY_SHEET_MIME_TYPES = [
  'application/vnd.ms-excel',
  'application/vnd.oasis.opendocument.spreadsheet',
];
const TIFF_MIME_TYPE = 'image/tiff';
const DOWNLOAD_ONLY_MIME_TYPES = ['application/msword', 'image/heic', 'image/heif'];

function toDate(iso: string | null | undefined): Date | null {
  if (iso === null || iso === undefined) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** A finite number at least `min`, or `min` itself — `Math.max(0, NaN)` is `NaN`. */
function atLeast(value: number, min: number): number {
  return Number.isFinite(value) ? Math.max(min, value) : min;
}

function toText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** Lower-case, no leading dot, so `.PDF` and `pdf` pick the same glyph. */
export function toExtension(value: unknown): string {
  return toText(value).replace(/^\.+/, '').toLowerCase();
}

export function toGlyph(extension: string): FileGlyph {
  if (PDF_EXTENSIONS.includes(extension)) return 'pdf';
  if (SPREADSHEET_EXTENSIONS.includes(extension)) return 'spreadsheet';
  if ([...IMAGE_EXTENSIONS, ...TIFF_EXTENSIONS, ...HEIC_EXTENSIONS].includes(extension)) {
    return 'image';
  }
  if (VIDEO_EXTENSIONS.includes(extension)) return 'video';
  if (AUDIO_EXTENSIONS.includes(extension)) return 'audio';
  return 'document';
}

/** The extension's kind, when it names one; `null` sends the decision to the MIME type. */
function kindByExtension(extension: string): FilePreviewKind | null {
  if (DOWNLOAD_ONLY_EXTENSIONS.includes(extension)) return 'none';
  if (PDF_EXTENSIONS.includes(extension)) return 'pdf';
  if (TIFF_EXTENSIONS.includes(extension)) return 'tiff';
  if (IMAGE_EXTENSIONS.includes(extension)) return 'image';
  if (VIDEO_EXTENSIONS.includes(extension)) return 'video';
  if (AUDIO_EXTENSIONS.includes(extension)) return 'audio';
  if (DOCX_EXTENSIONS.includes(extension)) return 'docx';
  if (SHEET_EXTENSIONS.includes(extension)) return 'sheet';
  if (LEGACY_SHEET_EXTENSIONS.includes(extension)) return 'legacySheet';
  if (CSV_EXTENSIONS.includes(extension)) return 'csv';
  if (MARKDOWN_EXTENSIONS.includes(extension)) return 'markdown';
  if (TEXT_EXTENSIONS.includes(extension)) return 'text';
  return null;
}

function kindByMimeType(mime: string): FilePreviewKind {
  if (DOWNLOAD_ONLY_MIME_TYPES.includes(mime)) return 'none';
  if (mime === 'application/pdf') return 'pdf';
  if (mime === TIFF_MIME_TYPE) return 'tiff';
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime === DOCX_MIME_TYPE) return 'docx';
  if (mime === SHEET_MIME_TYPE) return 'sheet';
  if (LEGACY_SHEET_MIME_TYPES.includes(mime)) return 'legacySheet';
  if (CSV_MIME_TYPES.includes(mime)) return 'csv';
  if (MARKDOWN_MIME_TYPES.includes(mime)) return 'markdown';
  if (mime.startsWith('text/') || TEXT_MIME_TYPES.includes(mime)) return 'text';
  return 'none';
}

export function toPreviewKind(
  extension: string,
  mimeType: string,
  sizeBytes: number,
): FilePreviewKind {
  const kind = kindByExtension(extension) ?? kindByMimeType(mimeType.toLowerCase());
  // Text is laid out in one piece and the three parsers read the whole file on the main thread,
  // so past their caps the download is the better offer.
  if (kind === 'text' || kind === 'markdown') {
    return sizeBytes <= PREVIEW_TEXT_MAX_BYTES ? kind : 'none';
  }
  if (kind === 'csv' || kind === 'legacySheet') {
    return sizeBytes <= PREVIEW_SHEET_MAX_BYTES ? kind : 'none';
  }
  return kind;
}

export function toFileType(value: FileTypeDto | null | undefined): FileType | null {
  if (value === null || value === undefined) return null;
  return KNOWN_TYPES.find((type) => type === value) ?? 'unknown';
}

export function toLocation(value: string): FileLocation {
  return KNOWN_LOCATIONS.find((location) => location === value) ?? 'unknown';
}

export function toProvider(value: string): CloudProvider {
  return KNOWN_PROVIDERS.find((provider) => provider === value) ?? 'unknown';
}

export function toStatus(value: string): FileStatus {
  return KNOWN_STATUSES.find((status) => status === value) ?? 'unknown';
}

function toOrigin(value: FileOriginDto | null | undefined): FileOriginDto | null {
  if (value === null || value === undefined) return null;
  const labName = toText(value.labName);
  const labId = toText(value.labId);
  // An origin without a lab is not an origin the "Origin" column can draw.
  if (labName === '' || labId === '') return null;
  return { sessionId: toText(value.sessionId), labId, labName };
}

/** `name.ext`, or just the name when the extension is unknown — never a trailing dot. */
export function toFileName(name: string, extension: string): string {
  return extension === '' ? name : `${name}.${extension}`;
}

export function toFileRow(dto: FileListItemDto): FileRow {
  const name = toText(dto.name) || dto.id;
  const extension = toExtension(dto.extension);
  const mimeType = toText(dto.mimeType);
  const sizeBytes = Math.round(atLeast(dto.sizeBytes, 0));
  return {
    id: dto.id,
    name,
    extension,
    fileName: toFileName(name, extension),
    glyph: toGlyph(extension),
    previewKind: toPreviewKind(extension, mimeType, sizeBytes),
    mimeType,
    sizeBytes,
    createdAt: toDate(dto.createdAt),
    type: toFileType(dto.type),
    origin: toOrigin(dto.origin),
    located: toLocation(dto.located),
    status: toStatus(dto.status),
  };
}

/**
 * The banner needs the file's name for the per-file kinds ("**Calibration report** was modified
 * by another app…"); the envelope names it by id, so it is resolved against the page's rows. A
 * file on another page resolves to `null`, and the banner then omits the name rather than
 * inventing one.
 */
function toNotice(dto: FileNoticeDto, rows: FileRow[]): FileNotice | null {
  const kind = KNOWN_NOTICE_KINDS.find((known) => known === dto.kind);
  // A kind the portal cannot word is dropped — a banner with no sentence is worse than none.
  if (kind === undefined) return null;
  const fileId = typeof dto.fileId === 'string' && dto.fileId !== '' ? dto.fileId : null;
  return {
    kind,
    provider: toProvider(dto.provider),
    fileId,
    fileName: fileId === null ? null : (rows.find((row) => row.id === fileId)?.name ?? null),
  };
}

export function toFilesView(dto: FileListDto): FilesView {
  const rows = (Array.isArray(dto.items) ? dto.items : []).map(toFileRow);
  const pageSize = Math.max(1, Math.round(atLeast(dto.pageSize, 1)));
  const totalCount = Math.round(atLeast(dto.totalCount, 0));
  const totalUnfiltered = Math.round(atLeast(dto.totalUnfiltered, totalCount));
  return {
    rows,
    page: Math.max(FIRST_PAGE, Math.round(atLeast(dto.page, FIRST_PAGE))),
    pageSize,
    totalCount,
    totalPages: Math.max(1, Math.ceil(totalCount / pageSize)),
    totalUnfiltered,
    isEmpty: rows.length === 0,
    hasNoFilesAtAll: totalUnfiltered === 0,
    notices: (dto.notices ?? [])
      .map((notice) => toNotice(notice, rows))
      .filter((notice): notice is FileNotice => notice !== null),
  };
}

/**
 * Whether a selection is over either cap and must not be sent.
 *
 * `bytes` is `null` for a "select all N" selection, whose size only the server knows — the count
 * is still checked, because `totalCount` is honest about how many files that is.
 */
export function exceedsSelectionCap(count: number, bytes: number | null): boolean {
  if (count > MAX_SELECTION_FILES) return true;
  return bytes !== null && bytes > MAX_SELECTION_BYTES;
}

export function toStorageLevel(percent: number): StorageLevel {
  if (percent >= STORAGE_EXCEEDED_PERCENT) return 'exceeded';
  if (percent >= STORAGE_WARNING_PERCENT) return 'warning';
  return 'ok';
}

/**
 * The percentage is **not clamped** — the design draws "102 %" — but a zero quota is treated as
 * fully used rather than as a division by zero: a user with no allowance has no room.
 */
export function toStorageView(dto: StorageDto): StorageView {
  const usedBytes = Math.round(atLeast(dto.usedBytes, 0));
  const quotaBytes = Math.round(atLeast(dto.quotaBytes, 0));
  const percent =
    quotaBytes === 0 ? FULL_PERCENT : Math.round((usedBytes / quotaBytes) * FULL_PERCENT);
  return { usedBytes, quotaBytes, percent, level: toStorageLevel(percent) };
}

export function toDownloadTicket(dto: DownloadDto): DownloadTicket {
  return {
    url: toText(dto.url),
    expiresAt: toDate(dto.expiresAt),
    fileName: toText(dto.fileName),
    mimeType: toText(dto.mimeType),
    sizeBytes: Math.round(atLeast(dto.sizeBytes, 0)),
  };
}

/**
 * A ticket URL on the API's own host, in the API's own scheme — a **workaround** (HS-1474).
 *
 * Stage answers `GET …/files/{id}/download` with an `http://` URL on its `https://` host: the API
 * builds it from `Request.Scheme`, which reads `http` behind the TLS-terminating proxy unless that
 * proxy forwards `X-Forwarded-Proto` (the API honours it since HS-1483). The `http://` byte route
 * answers 503, so every preview and every download failed; the same token over `https://` answers
 * the bytes. A deployed page's CSP (`connect-src 'self' https:`) would refuse the `http://` fetch
 * anyway.
 *
 * Only a downgrade on the **same host** as the configured API is corrected. Any other host or
 * scheme is left exactly as the server sent it, so this cannot point a ticket — a bearer token in
 * all but name — anywhere the backend did not. Delete with HS-1525, the backend fix.
 */
export function toSecureTicketUrl(ticketUrl: string, apiBaseUrl: string): string {
  let ticket: URL;
  let api: URL;
  try {
    ticket = new URL(ticketUrl);
    api = new URL(apiBaseUrl);
  } catch {
    return ticketUrl;
  }
  // An explicit port on the ticket would name a different listener; only the default is fixed.
  const sameHost = ticket.hostname === api.hostname && ticket.port === '';
  if (ticket.protocol !== 'http:' || api.protocol !== 'https:' || !sameHost) return ticketUrl;
  ticket.protocol = api.protocol;
  return ticket.toString();
}

export function toDeleteOutcome(value: string): DeleteOutcome {
  return KNOWN_OUTCOMES.find((outcome) => outcome === value) ?? 'unknown';
}

export function toDeleteResults(dto: DeleteManyDto): DeleteResult[] {
  return (Array.isArray(dto.results) ? dto.results : []).map((result) => ({
    id: result.id,
    name: toText(result.name) || result.id,
    outcome: toDeleteOutcome(result.outcome),
  }));
}

export function toMoveOutcome(value: string): MoveOutcome {
  return KNOWN_MOVE_OUTCOMES.find((outcome) => outcome === value) ?? 'unknown';
}

export function toMoveResults(dto: MoveManyDto): MoveResult[] {
  return (Array.isArray(dto.results) ? dto.results : []).map((result) => ({
    id: result.id,
    name: toText(result.name) || result.id,
    outcome: toMoveOutcome(result.outcome),
  }));
}

/**
 * One folder of the tree (HS-1724). A blank name falls back to the id, as a file's does, so a row
 * and a breadcrumb never render empty; a count that is not a finite number reads as none.
 */
export function toFolder(dto: FolderDto): Folder {
  return {
    id: dto.id,
    name: toText(dto.name) || dto.id,
    parentFolderId:
      dto.parentFolderId === null || dto.parentFolderId === '' ? null : dto.parentFolderId,
    fileCount: Math.round(atLeast(dto.fileCount, 0)),
    createdAt: toDate(dto.createdAt),
  };
}

/** A count that is not a finite number reads as none; a negative one as zero. */
function toCount(value: number): number {
  return Math.round(atLeast(value, 0));
}

function toNodeCounts(dto: NodeCountsDto): NodeCountsDto {
  return {
    files: toCount(dto.files),
    folders: toCount(dto.folders),
    directFiles: toCount(dto.directFiles),
    directFolders: toCount(dto.directFolders),
  };
}

/**
 * The counts endpoint (HS-1763) as a lookup. The first entry for an id wins, as in the tree, and
 * an absent `folders` list is simply no per-folder figures — every row then falls back to its own
 * direct count.
 */
export function toFileCounts(dto: FileCountsDto): FileCounts {
  const byFolder = new Map<string, NodeCountsDto>();
  for (const entry of Array.isArray(dto.folders) ? dto.folders : []) {
    if (entry.id !== '' && !byFolder.has(entry.id)) byFolder.set(entry.id, toNodeCounts(entry));
  }
  return { root: toNodeCounts(dto.root), byFolder };
}
